import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Zap, Eye, EyeOff, LogIn } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

export default function AdminLogin() {
  const [form, setForm] = useState({ email: '', password: '' });
  const [showPass, setShowPass] = useState(false);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/api/admin/login`, form);
      localStorage.setItem('biovex_admin_token', data.token);
      navigate('/admin/dashboard');
    } catch {
      toast.error('Invalid credentials. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-bvx-bg flex items-center justify-center px-4">
      <div
        className="absolute inset-0 pointer-events-none"
        style={{ background: 'radial-gradient(ellipse 60% 60% at 50% 50%, rgba(0,240,255,0.06) 0%, transparent 70%)' }}
      />
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="glass-card p-10 w-full max-w-sm relative z-10"
        data-testid="admin-login-form"
      >
        {/* Logo */}
        <div className="flex items-center gap-2 mb-8">
          <div className="w-8 h-8 rounded-lg bg-bvx-cyan/20 border border-bvx-cyan/40 flex items-center justify-center">
            <Zap size={16} className="text-bvx-cyan" />
          </div>
          <span className="font-outfit font-bold text-white text-base">BIOVEX<span className="text-bvx-cyan">.</span></span>
        </div>

        <h1 className="font-outfit font-bold text-white text-2xl mb-1">Admin Access</h1>
        <p className="text-white/40 text-sm font-inter-tight mb-8">Sign in to manage your site content.</p>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block font-rajdhani font-semibold uppercase tracking-widest text-white/40 text-xs mb-2">
              Email
            </label>
            <input
              type="email"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              placeholder="admin@biovex.tech"
              data-testid="admin-email-input"
              className="w-full h-12 px-4 rounded-lg bg-white/5 border border-white/10 focus:border-bvx-cyan/50 focus:outline-none text-white text-sm font-inter-tight placeholder:text-white/25 transition-colors duration-200"
            />
          </div>
          <div>
            <label className="block font-rajdhani font-semibold uppercase tracking-widest text-white/40 text-xs mb-2">
              Password
            </label>
            <div className="relative">
              <input
                type={showPass ? 'text' : 'password'}
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                placeholder="••••••••"
                data-testid="admin-password-input"
                className="w-full h-12 px-4 pr-11 rounded-lg bg-white/5 border border-white/10 focus:border-bvx-cyan/50 focus:outline-none text-white text-sm font-inter-tight placeholder:text-white/25 transition-colors duration-200"
              />
              <button
                type="button"
                onClick={() => setShowPass(!showPass)}
                data-testid="toggle-password"
                className="absolute right-3 top-1/2 -translate-y-1/2 text-white/30 hover:text-white/70 transition-colors duration-200"
              >
                {showPass ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
          </div>
          <button
            type="submit"
            disabled={loading}
            className="btn-primary w-full flex items-center justify-center gap-2 mt-2 disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="admin-login-btn"
          >
            <LogIn size={15} />
            {loading ? 'Signing in...' : 'Sign In'}
          </button>
        </form>
      </motion.div>
    </div>
  );
}
